import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, ArrowRight, ArrowLeft } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import WhatsAppFloat from "../WhatsAppFloat";
import PageMeta from "../PageMeta";
import { getFeatureBySlug, featurePages } from "../../data/pageConfig";

export default function FeaturePage() {
  const { featureSlug, lang } = useParams();
  const { t } = useTranslation("features");
  const navigate = useNavigate();
  const feature = getFeatureBySlug(featureSlug);

  if (!feature) {
    navigate(`/${lang || "en"}`, { replace: true });
    return null;
  }

  const Icon = feature.icon;
  const prefix = lang ? `/${lang}` : "";
  const currentLang = lang || "en";

  const benefits = t(`${feature.key}.benefits`, { returnObjects: true });
  const howItWorks = t(`${feature.key}.howItWorks`, { returnObjects: true });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `https://trackteam.app/${currentLang}` },
      { "@type": "ListItem", "position": 2, "name": t("index.title"), "item": `https://trackteam.app/${currentLang}/features` },
      { "@type": "ListItem", "position": 3, "name": t(`${feature.key}.title`) },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={`${t(`${feature.key}.title`)} — TrackTeam`}
        description={t(`${feature.key}.description`)}
        schema={breadcrumbSchema}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-slate-50 via-blue-50/40 to-purple-50/30 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }}></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <Link to={`${prefix}/features`} className="inline-flex items-center gap-2 text-[#62329f] hover:underline mb-6 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            {t("backToFeatures")}
          </Link>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center ${feature.shadowColor} shadow-lg`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight">
              {t(`${feature.key}.title`)}
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t(`${feature.key}.description`)}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://apps.apple.com/ae/app/trackteam/id6755401884"
              target="_blank"
              rel="noreferrer"
              className="inline-flex"
            >
              <img src="/Image/logo_appstore.svg" alt="App Store" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share"
              target="_blank"
              rel="noreferrer"
              className="inline-flex"
            >
              <img src="/Image/logo_playstore.svg" alt="Google Play" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {t(`${feature.key}.benefitsTitle`)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.isArray(benefits) &&
              benefits.map((benefit, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-lg transition-shadow">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4`}>
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.text}</p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {t(`${feature.key}.howItWorksTitle`)}
          </h2>
          <div className="space-y-8">
            {Array.isArray(howItWorks) &&
              howItWorks.map((step, i) => (
                <div key={i} className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#62329f] flex items-center justify-center text-white font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.text}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Other Features */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t("otherFeatures")}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {featurePages
              .filter((f) => f.slug !== featureSlug)
              .map((f) => {
                const FIcon = f.icon;
                return (
                  <Link
                    key={f.slug}
                    to={`${prefix}/features/${f.slug}`}
                    className="flex flex-col items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors text-center"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center`}>
                      <FIcon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{t(`${f.key}.title`)}</span>
                  </Link>
                );
              })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#62329f] via-purple-700 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("cta.title")}</h2>
          <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto">{t("cta.subtitle")}</p>
          <div className="flex justify-center gap-4">
            <a href="https://apps.apple.com/ae/app/trackteam/id6755401884" target="_blank" rel="noreferrer">
              <img src="/Image/logo_appstore.svg" alt="App Store" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share" target="_blank" rel="noreferrer">
              <img src="/Image/logo_playstore.svg" alt="Google Play" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
